/* eslint-disable @nx/enforce-module-boundaries */
import {
  Avatar,
  Box,
  Divider,
  HStack,
  Heading,
  Link,
  SeeMore,
  Stack,
  Text,
} from '@mybridge/ui';
import moment from 'moment';
import { forwardRef, memo, useContext, useMemo, useState } from 'react';
import { EMPLOYMENT_CHOICES } from 'v4/lib/data';
import { PublicProfileContext } from '../context';
import { ShowAllLink } from '../show-all-link';
import { formatCommaSeparated } from 'v4/lib/commons';
import { MyNetworkContext } from 'v4/components/network/context';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useSelector } from 'react-redux';
import { EducationIcon } from '@mybridge/icons';

export const MyProfileEducation = forwardRef(({ ...props }, ref) => {
  const { education, entityHasStartDates, entityHasEndDates, publicProfile } =
    useContext(PublicProfileContext);
  const { t } = useTranslator();
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const { isFriend } = useContext(MyNetworkContext);
  const [showAll, setShowAll] = useState(false);

  // Filter visible education
  const visibleEducation = useMemo(() => {
    return education?.filter(
      (edu) =>
        edu?.audience === 1 || (edu?.audience === 2 && isFriend(publicProfile))
    ) ?? [];
  }, [education, isFriend, publicProfile]);

  const allEducationPrivate = useMemo(() => {
    return (
      education?.length > 0 &&
      education?.every((edu) => edu?.audience === 2 || edu?.audience === 3) &&
      !isFriend(publicProfile)
    );
  }, [education, isFriend, publicProfile]);

  const schools = useMemo(
    () => visibleEducation?.filter((me) => me?.type === 'School') ?? [],
    [visibleEducation]
  );

  const colleges = useMemo(
    () => visibleEducation?.filter((me) => me?.type !== 'School') ?? [],
    [visibleEducation]
  );

  // Group colleges by name
  const groupedColleges = useMemo(() => {
    const myExps = {};
    if (!Array.isArray(colleges)) return {};

    const sortedColleges = [...colleges].sort((a, b) => {
      const dateA = new Date(a.start_year, a.start_month || 0);
      const dateB = new Date(b.start_year, b.start_month || 0);
      return dateB - dateA; // newest first
    });

    for (let me of sortedColleges) {
      const normalizedName = me?.name?.trim().toLowerCase();
      if (myExps[normalizedName]) {
        myExps[normalizedName].push(me);
      } else {
        myExps[normalizedName] = [me];
      }
    }

    Object.values(myExps).forEach((group) => {
      group.sort((a, b) => {
        const dateA = new Date(a.start_year, a.start_month || 0);
        const dateB = new Date(b.start_year, b.start_month || 0);
        return dateB - dateA;
      });
    });

    return myExps;
  }, [colleges]);

  // Group schools by name
  const groupedSchools = useMemo(() => {
    const myExps = {};
    if (!Array.isArray(schools)) return {};

    const sortedSchools = [...schools].sort((a, b) => {
      const dateA = new Date(a.start_year, a.start_month || 0);
      const dateB = new Date(b.start_year, b.start_month || 0);
      return dateB - dateA; // newest first
    });

    for (let me of sortedSchools) {
      const normalizedName = me?.name?.trim().toLowerCase();
      if (myExps[normalizedName]) {
        myExps[normalizedName].push(me);
      } else {
        myExps[normalizedName] = [me];
      }
    }

    Object.values(myExps).forEach((group) => {
      group.sort((a, b) => {
        const dateA = new Date(a.start_year, a.start_month || 0);
        const dateB = new Date(b.start_year, b.start_month || 0);
        return dateB - dateA;
      });
    });

    return myExps;
  }, [schools]);

  // Timeline item component
  const Item = memo(
    ({
      exp,
      hasBar = false,
      isLast = false,
      hasTitle = false,
      isSchool = false,
      showActions = true,
    }) => {
      return (
        <Stack spacing={0} pt={hasTitle ? 4 : 0} pos="relative">
          {hasBar && (
            <>
              <Box
                pos="absolute"
                w="20px"
                h="20px"
                top={4}
                left={'-40px'}
                bg="brandGray.300"
                borderRadius="full"
                zIndex={1}
              />
              <Box pos="absolute" w="1" top={4} bottom={-4} left={-8} bg="brandGray.300" />
            </>
          )}
          {isLast && (
            <Box
              pos="absolute"
              w="20px"
              h="20px"
              top={4}
              left={'-40px'}
              bg="brandGray.300"
              borderRadius="full"
              zIndex={1}
            />
          )}
          <HStack alignItems="flex-start">
            <Stack spacing={0} mt={5} flex={1}>
              {exp?.degree && (
                <Text fontWeight="500" fontSize="md">
                  {exp.degree}
                </Text>
              )}

              {exp?.courses?.length > 0 && exp?.courses?.split?.(',')?.length && (
                <Text fontSize="sm" color="gray.600">
                  Courses: {formatCommaSeparated(exp.courses.split(','))}
                </Text>
              )}

              <Box fontSize="sm">
                {entityHasStartDates(exp) || entityHasEndDates(exp) ? (
                  <Text display="inline">
                    {[
                      [
                        moment()
                          .set('M', exp?.start_month - 1)
                          .set('Y', exp?.start_year)
                          .format('MMMM YYYY'),
                        exp?.end_month && exp?.end_year
                          ? moment()
                              .set('M', exp?.end_month - 1)
                              .set('Y', exp?.end_year)
                              .format('MMMM YYYY')
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' - '),
                      exp?.is_graduate ? t('education.graduated') || 'Graduated' : null,
                    ]
                      .filter(Boolean)
                      .join(' | ')}
                  </Text>
                ) : (
                  ''
                )}
                {exp?.location?.length && (
                  <>
                    <Text display="inline">&nbsp;·&nbsp;</Text>
                    <Text display="inline">{exp?.location}</Text>
                  </>
                )}
              </Box>

              <Box fontSize="sm">
                {formatCommaSeparated(
                  [
                    exp?.industry,
                    EMPLOYMENT_CHOICES?.find((ec) => ec.value === exp?.employment_type)
                      ?.label,
                  ],
                  ' · '
                )}
              </Box>

              <Stack py={2}>{exp?.desc && <SeeMore text={exp?.desc} />}</Stack>
            </Stack>
          </HStack>
        </Stack>
      );
    }
  );

  if (!education?.length) return <></>;

  const shouldShowHeading = !allEducationPrivate && visibleEducation?.length > 0;

  return (
    <Stack>
      {shouldShowHeading && (
        <HStack alignItems="flex-start">
          <Stack flex={1}>
            <Heading fontSize="18px" color="brandPrimary.500" fontWeight="400">
              {t('education.education') || 'Education'}
            </Heading>
          </Stack>
        </HStack>
      )}

      {allEducationPrivate ? (
        <HStack>
          <EducationIcon height="20px" width="20px" color="#868585" />
          <Text color="#868585">
            {t('otherModules.noEducationToShow') || 'No education to show'}
          </Text>
        </HStack>
      ) : visibleEducation?.length ? (
        <>
          {/* Colleges */}
          {Object.keys(groupedColleges)?.length ? (
            <>
              <Heading color="brandPrimary.500" size="sm" fontWeight="400">
                {t('education.college') || 'College'}
              </Heading>
              <Stack pt={4} spacing={6}>
                {Object.entries(groupedColleges)
                  ?.filter?.((_, ind) => showAll || ind < 3)
                  ?.map?.(([uniName, expArr], expIndex) => {
                    const exp = expArr[0];
                    const displayName = exp?.name;
                    return (
                      <Stack key={expIndex}>
                        <HStack alignItems="flex-start">
                          <Avatar size="md" name={displayName} />
                          <Stack spacing={0} flex={1}>
                            <Box>
                              <Text display="inline" fontWeight="600" fontSize="lg">
                                {displayName}
                              </Text>
                              {expArr?.length > 1 && (
                                <Text fontSize="sm">
                                  {t('education.university') || 'University'}
                                </Text>
                              )}
                            </Box>

                            {expArr.length <= 1 ? (
                              <Item exp={exp} key={expIndex} />
                            ) : (
                              <Stack spacing={0} mt={4} pl={4}>
                                {expArr.map((exp_, expIndex_) => (
                                  <Item
                                    hasTitle={false}
                                    hasBar={expIndex_ < expArr.length - 1}
                                    exp={exp_}
                                    key={expIndex_}
                                    isLast={expIndex_ === expArr.length - 1}
                                  />
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        </HStack>

                        {Object.keys(groupedColleges).length > 1 &&
                        expIndex < Object.keys(groupedColleges).length - 1 ? (
                          <Divider mt={4} />
                        ) : null}
                      </Stack>
                    );
                  })}
              </Stack>
            </>
          ) : null}

          {/* Schools */}
          {Object.keys(groupedSchools)?.length ? (
            <>
              <Divider />
              <Heading mt={4} color="brandPrimary.500" size="sm" fontWeight="400">
                {t('education.school') || 'School'}
              </Heading>
              <Stack pt={4} spacing={6}>
                {Object.entries(groupedSchools)
                  ?.filter?.((_, ind) => showAll || ind < 3)
                  ?.map?.(([schoolName, expArr], expIndex) => {
                    const exp = expArr[0];
                    const displayName = exp?.name;
                    return (
                      <Stack key={expIndex}>
                        <HStack alignItems="flex-start">
                          <Avatar size="md" name={displayName} />
                          <Stack spacing={0} flex={1}>
                            <Box>
                              <Text display="inline" fontWeight="600" fontSize="lg">
                                {displayName}
                              </Text>
                              { (
                                <Text fontSize="sm">{t('education.school') || 'School'}</Text>
                              )}
                            </Box>

                            {expArr.length <= 1 ? (
                              <Item exp={exp} key={expIndex} isSchool />
                            ) : (
                              <Stack spacing={0} mt={4} pl={4}>
                                {expArr.map((exp_, expIndex_) => (
                                  <Item
                                    hasTitle={false}
                                    hasBar={expIndex_ < expArr.length - 1}
                                    exp={exp_}
                                    key={expIndex_}
                                    isLast={expIndex_ === expArr.length - 1}
                                    isSchool
                                  />
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        </HStack>

                        {Object.keys(groupedSchools).length > 1 &&
                        expIndex < Object.keys(groupedSchools).length - 1 ? (
                          <Divider mt={4} />
                        ) : null}
                      </Stack>
                    );
                  })}
              </Stack>
            </>
          ) : null}

          {!showAll && visibleEducation?.length > 3 && (
            <ShowAllLink onClick={() => setShowAll(true)} />
          )}
        </>
      ) : (
        <HStack justifyContent="center">
          {publicProfile?.id === userProfileInfo?.id ? (
            <Link>{t('publicProfile.showOffEducationJourney') || 'Show off your educational journey'}</Link>
          ) : (
            <>
              <EducationIcon height="21px" width="21px" color="#868585" />
              <Text color="#868585">{t('otherModules.noEducationToShow') || 'No education to show'}</Text>
            </>
          )}
        </HStack>
      )}
    </Stack>
  );
});
